import React, {
  useState,
  useEffect,
  SetStateAction,
  Dispatch,
  useContext,
  useMemo,
  useRef,
} from "react";
import { PeopleData } from "../../types";
import TouchableOpacity from "../../components/general/TouchableOpacity";
import { HorizontalLeftAlignedContainer } from "../../styles/container";
import {
  ITEM_HORIZONTAL_SPACING,
  ITEM_VERTICAL_SPACING,
  LINE_HORIZONTAL_SPACING,
  LINE_VERTICAL_SPACING,
} from "../../styles/spacing";
import { userContext } from "../../UserContext";
import { MemberSelectionItem } from "../../components/people/MemberPickerListItem";
import { EditActionBar } from "../actionBars/EditActionBar";
import { UseQueryResult } from "react-query";
import { useWindowHeight } from "@react-hook/window-size";

const alphabetizePeopleDataArray = (people: PeopleData[]) => {
  people.sort((a, b) => {
    if (a.lastName > b.lastName) {
      return 1;
    } else if (a.lastName === b.lastName) {
      if (a.firstName > a.lastName) return 1;
      else return -1;
    } else {
      return -1;
    }
  });
};

type RecipientPickerProps = {
  friendsQueryResult: UseQueryResult<PeopleData[]>;
  isOpen: boolean;
  closePicker: () => void;
  pickedRecipients: PeopleData[];
  setPickedRecipients: Dispatch<SetStateAction<PeopleData[]>>;
  ineligible?: string[];
};

export const RecipientPicker = (props: RecipientPickerProps) => {
  const {
    friendsQueryResult,
    isOpen,
    closePicker,
    pickedRecipients,
    setPickedRecipients,
    ineligible,
  } = props;
  const { isSuccess, data } = friendsQueryResult;

  const userInfo = useContext(userContext);
  const ignoreItems = useMemo(() => {
    return ineligible ? ineligible : [];
  }, [ineligible]);
  const thisUser: PeopleData = {
    userId: userInfo.id,
    firstName: userInfo.firstName,
    lastName: userInfo.lastName,
    imageUri: userInfo.imageUri,
  };
  const [checkedState, setCheckedState] = useState<boolean[]>([]);
  const [visibleState, setVisibleState] = useState<boolean[]>([]);
  const [suggestionItems, setSuggestionItems] = useState<PeopleData[]>([]);
  const [memberSearchText, setMemberSearchText] = useState("");
  const divRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(500);

  const windowHeight = useWindowHeight();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMemberSearchText(e.target.value);
    if (!data) return;
    const updatedVisibleState: boolean[] = [];
    const regex = new RegExp(e.target.value, "ig");

    suggestionItems.forEach((suggestion) => {
      const result = (suggestion.firstName + " " + suggestion.lastName).match(
        regex
      );
      updatedVisibleState.push(
        !!result && !ignoreItems.includes(suggestion.userId)
      );
    });
    setVisibleState(updatedVisibleState);
  };

  const handleRecipientSelection = (
    selectedPerson: PeopleData,
    selectedIndex: number
  ) => {
    const updatedCheckedState = checkedState.map((checked, index) =>
      index === selectedIndex ? !checked : checked
    );
    setCheckedState(updatedCheckedState);
  };

  const onCancel = () => {
    const newVisibleState: boolean[] = new Array(suggestionItems.length).fill(
      true
    );
    setVisibleState(newVisibleState);
    closePicker();
  };

  const onContinue = () => {
    const newPickedList: PeopleData[] = [thisUser];
    suggestionItems.forEach((suggestion, index) => {
      if (checkedState[index]) {
        newPickedList.push({ ...suggestion });
      } else {
      }
    });
    setPickedRecipients(newPickedList);
    closePicker();
  };

  useEffect(() => {
    if (isSuccess && data) {
      const alphabetizedData = data;
      alphabetizePeopleDataArray(alphabetizedData);
      setSuggestionItems(alphabetizedData);
    } else {
      setSuggestionItems([]);
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (isOpen) {
      setMemberSearchText("");
      const newCheckedState: boolean[] = [];
      const newVisibleState: boolean[] = [];
      suggestionItems.forEach((suggestion) => {
        const selectedInitially = pickedRecipients.find(
          (item: PeopleData) => item.userId === suggestion.userId
        );

        newCheckedState.push(!!selectedInitially);
        newVisibleState.push(!ignoreItems.includes(suggestion.userId));
      });
      setCheckedState(newCheckedState);
      setVisibleState(newVisibleState);
    }
  }, [isOpen, ignoreItems, pickedRecipients, suggestionItems]);

  const renderSuggestionItem = (item: PeopleData, index: number) => {
    return (
      <HorizontalLeftAlignedContainer
        key={index}
        style={{
          width: "100%",
          marginRight: ITEM_HORIZONTAL_SPACING,
          marginBottom: LINE_VERTICAL_SPACING,
          display: visibleState[index] === false ? "none" : undefined,
        }}
      >
        <TouchableOpacity
          style={{
            flex: 1,
            overflow: "hidden",
            borderStyle: "none",
            backgroundColor: "white",
          }}
          onClick={() => handleRecipientSelection(item, index)}
        >
          <HorizontalLeftAlignedContainer
            style={{ paddingBottom: LINE_HORIZONTAL_SPACING }}
          >
            <input
              type="checkbox"
              checked={checkedState[index]}
              onChange={() => handleRecipientSelection(item, index)}
              style={{ width: 24 }}
            />
            <MemberSelectionItem
              firstName={item.firstName}
              lastName={item.lastName}
              imageUri={item.imageUri}
              isPicked={false}
            />
          </HorizontalLeftAlignedContainer>
        </TouchableOpacity>
      </HorizontalLeftAlignedContainer>
    );
  };
  useEffect(() => {
    if (!divRef.current) return;
    setHeight(divRef.current.offsetHeight);
  }, []);

  return (
    <div
      style={{
        marginTop: 20,
        marginLeft: 10,
        marginRight: 10,
        marginBottom: 20,
      }}
    >
      <div ref={divRef}>
        <EditActionBar onCancel={onCancel} onSave={onContinue} />

        <HorizontalLeftAlignedContainer
          style={{ marginBottom: ITEM_VERTICAL_SPACING, width: "100%" }}
        >
          <input
            type="text"
            value={memberSearchText}
            placeholder="Type any part of the name."
            onChange={onChange}
            maxLength={35}
            style={{ width: "100%", marginRight: 25 }}
          />
        </HorizontalLeftAlignedContainer>
      </div>
      <div
        id="scrollableDiv"
        style={{
          height: windowHeight - height - 55,
          overflow: "auto",
        }}
      >
        {isSuccess && data && suggestionItems && (
          <div style={{ flexDirection: "row" }}>
            {suggestionItems.map((suggestion: PeopleData, index: number) => {
              return renderSuggestionItem(suggestion, index);
            })}
          </div>
        )}
      </div>
    </div>
  );
};
